/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  type AbsoluteURL,
  coreServiceToFlow,
  FlowRunState,
  type FlowRunStatus,
  paFlows,
} from '@poweralpha/common';
import { useCallback } from 'react';
import { getBatchRunLink } from '../../home/links';
import { getPlantSweepPath } from '../../plant-v2/paths';
import usePANavigate from '../../react-components/navigate/usePANavigate';
import { useGetScenarioIdMutation } from '../api';
import { getRecordLink } from '../utils';

/** Flows whose result record is addressed by scenario id, looked up from the run id. */
const scenarioIdFlows = new Set<string>([
  ...paFlows,
  ...Object.values(coreServiceToFlow),
  'PlantSim v2',
]);

const getRunLink = (run: FlowRunStatus): AbsoluteURL | null => {
  // A PlantSim sweep's page is addressed by its run id, both while it runs and after.
  if (run.flow_name === 'PlantSim v2 Batch') return getPlantSweepPath(run.id);
  if (run.state.type !== FlowRunState.COMPLETED) return getBatchRunLink(run);
  try {
    return getRecordLink(run);
  } catch {
    return null;
  }
};

/**
 * Where a run row leads: a finished run opens its result record, a running batch its batch
 * page. Returns null for runs without a destination.
 */
export function useOpenRunRecord(onOpen: () => void) {
  const { navigate } = usePANavigate();
  const { mutateAsync: getScenarioIds } = useGetScenarioIdMutation();

  const canOpen = useCallback(
    (run: FlowRunStatus) =>
      (run.state.type === FlowRunState.COMPLETED && scenarioIdFlows.has(run.flow_name)) ||
      getRunLink(run) !== null,
    [],
  );

  const open = useCallback(
    async (run: FlowRunStatus) => {
      if (run.state.type === FlowRunState.COMPLETED && scenarioIdFlows.has(run.flow_name)) {
        const [record] = await getScenarioIds([run.id]);
        if (!record) return;
        navigate(getRecordLink(run, { scenarioId: record.scenario_id }));
        onOpen();
        return;
      }
      const link = getRunLink(run);
      if (!link) return;
      navigate(link);
      onOpen();
    },
    [getScenarioIds, navigate, onOpen],
  );

  return { canOpen, open };
}
